import { FunctionComponent, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";

import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { classNames } from "primereact/utils";

import { TooltipWrapper } from "../../Components/TooltipWrapper/TooltipWrapper";

import "./ProgressButton.scss";

interface IProgressButtonProps {
  className?: string;
  size?: "small" | "large" | undefined;
  progressText?: string | undefined;
  tooltip?: string;
  tooltipShowDelay?: number;
  tooltipPosition?: "top" | "bottom" | "left" | "right" | "mouse";
  tooltipDisabled?: boolean;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
}

const RawProgressButton: FunctionComponent<IProgressButtonProps> = (props: IProgressButtonProps) => {
  const buttonRef = useRef<Button | HTMLElement | null | undefined>(null);

  const [inProgress, setInProgress] = useState(false);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    setInProgress(!!props.progressText?.length);
  }, [props.progressText]);

  useEffect(() => {
    setDisabled(inProgress || !!props.disabled);
  }, [props.disabled, inProgress]);

  return (
    <div>
      <TooltipWrapper
        targetRef={buttonRef as React.RefObject<HTMLElement | null> | undefined}
        content={props.tooltip ?? ""}
        showOnDisabled={true}
        showDelay={props.tooltipShowDelay ?? 0}
        position={props.tooltipPosition ?? "top"}
        disabled={props.tooltipDisabled || !disabled}
      />

      <Button
        ref={buttonRef as React.Ref<Button> | undefined}
        className={classNames(`progress-button ${props.className}`)}
        size={props.size}
        onClick={event => !disabled && props.onClick?.(event)}
        disabled={disabled}
      >
        {inProgress ? (
          <span className="progress-button-content">
            <span>{props.progressText}</span>

            <ProgressSpinner className="inline-spinner" strokeWidth="6" />
          </span>
        ) : (
          props.children
        )}
      </Button>
    </div>
  );
};

export const ProgressButton = observer(RawProgressButton);
